import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Building2, Store, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ServiceData } from '@/data/servicesData';
import { scrollToHash } from '@/lib/scroll';

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: ServiceData | null;
}

export function ServiceModal({ isOpen, onClose, service }: ServiceModalProps) {
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      scrollToHash('#contact');
    }, 300);
  };

  if (!service) return null;

  const IconComponent = service.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - click to close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal Container - Flexbox centering */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col pointer-events-auto"
            >
              {/* Header - Compact */}
              <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="w-5 h-5 text-teal" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-navy-deep leading-tight">{service.title}</h2>
                  <p className="text-muted-foreground text-xs">{service.tagline}</p>
                </div>
                <button 
                  onClick={onClose} 
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              {/* Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
                {/* Overview */}
                <div>
                  <p className="text-muted-foreground leading-relaxed text-sm">{service.overview}</p>
                </div>

                {/* How It Works - Process Steps - 2x2 Grid */}
                <div>
                  <h3 className="text-xs font-semibold text-navy-deep uppercase tracking-wider mb-3">How It Works</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {service.processSteps.map((step, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="w-7 h-7 rounded-lg bg-teal/10 flex items-center justify-center text-teal font-bold text-xs flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-semibold text-navy-deep text-xs">{step.title}</h4>
                          <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Features - Compact 2-column */}
                <div>
                  <h3 className="text-xs font-semibold text-navy-deep uppercase tracking-wider mb-2">Key Features</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {service.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={index} className="flex gap-2 items-start">
                          <div className="w-6 h-6 rounded-md bg-orange/10 flex items-center justify-center flex-shrink-0">
                            <FeatureIcon className="w-3 h-3 text-orange" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-navy-deep text-xs">{feature.title}</h4>
                            <p className="text-[10px] text-muted-foreground leading-tight">{feature.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Who It's For - Inline */}
                <div>
                  <h3 className="text-xs font-semibold text-navy-deep uppercase tracking-wider mb-2">Who It's For</h3>
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-teal/10 rounded-md">
                      <Building2 className="w-3 h-3 text-teal" />
                      <span className="text-[10px] font-semibold text-teal">Gov:</span>
                      <span className="text-[10px] text-muted-foreground">{service.governmentUseCases}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-orange/10 rounded-md">
                      <Store className="w-3 h-3 text-orange" />
                      <span className="text-[10px] font-semibold text-orange">Biz:</span>
                      <span className="text-[10px] text-muted-foreground">{service.businessUseCases}</span>
                    </div>
                  </div>
                </div>

                {/* Channels/Platforms - Compact pills */}
                {service.channels && service.channels.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-navy-deep uppercase tracking-wider mb-2">
                      {service.id === 'subcontracting' ? 'NAICS Codes' : 'Channels'}
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {service.channels.map((channel, index) => (
                        <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full">
                          {channel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Use Case Examples - Compact list */}
                <div>
                  <h3 className="text-xs font-semibold text-navy-deep uppercase tracking-wider mb-2">Examples</h3>
                  <ul className="space-y-1">
                    {service.useCaseExamples.map((example, index) => (
                      <li key={index} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <div className="w-1 h-1 rounded-full bg-orange mt-1.5 flex-shrink-0" />
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Footer - Compact */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
                <a 
                  href="#contact" 
                  onClick={(e) => { e.preventDefault(); scrollToContact(); }}
                  className="text-teal hover:text-teal/80 text-xs font-medium transition-colors"
                >
                  Questions?
                </a>
                <Button 
                  onClick={scrollToContact}
                  size="sm"
                  className="bg-orange hover:bg-orange-hover text-white font-semibold"
                >
                  Get Started
                  <ArrowRight className="ml-1.5 w-3 h-3" />
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
